// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.Factories.js" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import { some, toArray, find, startsWith } from 'lodash/fp';

/**
 * Manage the factories of a type
 */
export default class Factories {
  /**
   * Create Factories object
   * @param {string} type Factory type
   * @param {boolean} cache Allow cached result
   */
  constructor(type: string, cache: boolean = false, cacheKeyResolver: (key, ...props) => string) {
    this.type = type;
    this.factories = new Map();
    this.cache = cache ? new Map() : null;
    this.cacheKeyResolver = cacheKeyResolver;
  }

  /**
   * Register a factory associated with a key
   * @param {string} key The key of factory
   * @param {Function} factory The factory function that returns the object
   */
  register(key: string, factory: (key: string, ...props: Array<any>) => Object): void {
    this.factories.set(key, factory);
  }

  /**
   * Determine whether a key has been registered with a factory
   * @param {string} key The factory key
   */
  containKey(key: string) {
    return this.factories.has(key);
  }

  /**
   * Determine whether a key has a base key which has been registered
   * @param {string} key The base key
   */
  containBaseKey(key: string) {
    let objKey = null;
    this.factories.forEach((value, factoryKey) => {
      if (!objKey && startsWith(key, factoryKey)) {
        objKey = factoryKey;
      }
    });
    return !!objKey;
  }

  /**
   * Get all defined values in the factories
   */
  values() {
    return [...this.factories.values()];
  }

  /**
   * Unregister a factory associated with a key
   * @param {string} key The factory key
   */
  unregister(key: string): void {
    this.factories.delete(key);
    this.cache && this.cache.delete(key);
  }

  /**
   * Reset the factories. All registrations are clean up.
   * This function is mostly used in unit test in order to reset the test data.
   */
  reset(): void {
    this.factories.clear();
    this.cache && this.cache.clear();
  }

  /**
   * Create an object by using a keyed factory with passing params
   * @param {string} key The factory key
   * @param {Array<any>} props The factory params
   */
  create(key: string, ...props: Array<any>): Object {
    ensure(this.factories.has(key), `${key} must be defined as a ${this.type}`);

    const cacheKey = (this.cacheKeyResolver && this.cacheKeyResolver(key, ...props)) || key;
    if (this.cache && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    const obj = this.factories.get(key)(key, ...props);

    this.cache && this.cache.set(key, obj);

    return obj;
  }

  /**
   * Create an object which is the base of the current key
   * @param {string} baseKey The key of base factory
   * @param {string} key The factory key
   * @param {Array<any>} props The factory params
   */
  createBase(baseKey: string, key: string, ...props: Array<any>): Object {
    const cacheKey = (this.cacheKeyResolver && this.cacheKeyResolver(key, ...props)) || key;
    if (cacheKey && this.cache && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    let objKey = null;
    this.factories.forEach((value, factoryKey) => {
      if (!objKey && startsWith(baseKey, factoryKey)) {
        objKey = factoryKey;
      }
    });

    const obj = this.factories.get(objKey)(key || objKey, ...props);

    this.cache && this.cache.set(key || objKey, obj);

    return obj;
  }
}



// WEBPACK FOOTER //
// ./ui/share/factories/Izenda.Factories.js